// Issue announcements: when a new digital issue is published, two draft
// emails are prepared — one for active members, one for members who expired
// in the last six months. Staff review both side by side, edit the copy,
// preview it as a member, send a test, then approve the send.
//
// Previews and tests use sample (non-personal) links on purpose: the server
// never mints a member's sign-in link for staff to look at.

import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, type MeResponse, type TestPool } from '../api/client'
import { Button } from '../components/Button'
import { apiErrorText } from '../lib/apiErrorText'
import { fmtDateTime, fmtInt } from '../lib/format'
import {
  AUDIENCE_LABEL, LIMITS, charCount, MAX_HIGHLIGHTS, canEdit, confirmPhrase, copyProblems, localInputToIso, moveHighlight,
  type AnnouncementDetail, type AudienceDetail, type Copy, type Highlight,
} from '../lib/issueAnnouncements'
import { TITLE_LABELS, TITLE_ORDER } from '../lib/supportDesk'

type ListItem = {
  edition_id: number; title: string; issue_label: string; cover_image_url: string | null
  publication_slug: string; created_at: string | null
  audiences: Partial<Record<'active' | 'expired', { id: number; status: string | null }>>
}
type Edition = {
  edition_id: number; title: string; issue_label: string; cover_image_url: string | null
  publication_slug: string; has_announcements: boolean
}
type Count = { count: number; sendable_count: number; estimated_minutes: number }
type Sample = { items: Array<{ id: number; email: string | null; first_name: string | null; last_name: string | null }> }
type Preview = { to: string; from_email: string; subject: string; html: string; text: string }
type TestResult = { ok: boolean; to_emails: string[]; sent_count: number; failed_count: number }

const STATUS_CHIP: Record<string, string> = {
  draft: 'border-paper-trace text-paper-dim',
  scheduled: 'border-radar-cyan/60 text-radar-cyan',
  running: 'border-amber-glow/60 text-amber-glow',
  completed: 'border-radar-green/60 text-radar-green',
  cancelled: 'border-paper-trace text-paper-trace',
  failed: 'border-radar-red/60 text-radar-red',
}

const ACTION = 'font-mono text-[11px] tracking-label uppercase border px-2.5 py-1 disabled:opacity-30'
const ACTION_GREEN = `${ACTION} border-radar-green/60 text-radar-green hover:bg-radar-green/10`
const ACTION_QUIET = `${ACTION} border-paper-trace text-paper-trace hover:text-paper hover:border-paper`
const INPUT = 'w-full bg-ink-900 border border-ink-500 px-2 py-1.5 font-mono text-[12px] text-paper focus:outline-none focus:border-amber-glow disabled:opacity-60'

function StatusChip({ status }: { status: string | null | undefined }) {
  const s = status ?? 'none'
  return (
    <span className={`font-mono text-[10px] tracking-label uppercase border px-1.5 py-0.5 ${STATUS_CHIP[s] ?? 'border-paper-trace text-paper-trace'}`}>
      {status ?? '—'}
    </span>
  )
}

function Cover({ url, className }: { url: string | null; className: string }) {
  return url
    ? <img src={url} alt="" className={`${className} object-cover border border-ink-500`} />
    : <div className={`${className} border border-ink-500 bg-ink-800`} />
}

function invalidateAll(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['issue-announcements'] })
  qc.invalidateQueries({ queryKey: ['ia-count'] })
}

export function IssueAnnouncementsPage() {
  const { editionId } = useParams()
  return editionId ? <ReviewView editionId={Number(editionId)} /> : <ListView />
}

function Header({ sub }: { sub: string }) {
  return (
    <header className="border-b border-ink-500 pb-6">
      <div className="label mb-1">Execution · email</div>
      <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
        Issue announcements
        <span className="text-amber-glow"> ∙ </span>
        <span className="italic font-light">{sub}</span>
      </h1>
    </header>
  )
}

// ---------------------------------------------------------------- list view

function ListView() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const list = useQuery({
    queryKey: ['issue-announcements'],
    queryFn: () => api.get<{ items: ListItem[] }>('/api/issue-announcements'),
  })
  const editions = useQuery({
    queryKey: ['issue-announcements', 'editions'],
    queryFn: () => api.get<Edition[]>('/api/issue-announcements/editions'),
  })
  const create = useMutation({
    mutationFn: (edition_id: number) => api.post<AnnouncementDetail>('/api/issue-announcements', { edition_id }),
    onSuccess: (_d, edition_id) => {
      invalidateAll(qc)
      navigate(`/issue-announcements/${edition_id}`)
    },
  })
  // Title filter lives in the URL so it survives opening a review and coming back.
  const [params, setParams] = useSearchParams()
  const title = params.get('title') ?? ''
  const present = new Set([
    ...(editions.data ?? []).map((e) => e.publication_slug),
    ...(list.data?.items ?? []).map((it) => it.publication_slug),
  ])
  const titles = [...TITLE_ORDER.filter((t) => present.has(t)), ...[...present].filter((t) => !(TITLE_ORDER as string[]).includes(t)).sort()]
  const shownEditions = editions.data?.filter((e) => !title || e.publication_slug === title)
  const shownItems = list.data?.items.filter((it) => !title || it.publication_slug === title)

  return (
    <div className="p-8 space-y-8 max-w-[1400px]">
      <Header sub="new-issue emails" />

      <div className="flex items-center gap-3">
        <span className="label">Title</span>
        <select className={`${INPUT} w-auto`} value={title}
                onChange={(e) => setParams(e.target.value ? { title: e.target.value } : {}, { replace: true })}>
          <option value="">All titles</option>
          {titles.map((t) => <option key={t} value={t}>{TITLE_LABELS[t] ?? t}</option>)}
        </select>
      </div>

      <section className="space-y-3">
        <div className="label">Announce an issue</div>
        {editions.isLoading && <div className="label text-paper-trace">Loading…</div>}
        {editions.error && <div className="font-mono text-[11px] text-radar-red">{apiErrorText(editions.error)}</div>}
        {editions.data && editions.data.length === 0 && (
          <div className="font-mono text-[11px] text-paper-trace">
            No published issues are ready to announce. An issue needs to be published with its pages ready, on a title open for member email.
          </div>
        )}
        {editions.data && editions.data.length > 0 && shownEditions?.length === 0 && (
          <div className="font-mono text-[11px] text-paper-trace">No issues ready to announce for this title.</div>
        )}
        {create.error && <div className="font-mono text-[11px] text-radar-red">{apiErrorText(create.error)}</div>}
        <div className="flex flex-wrap gap-3">
          {shownEditions?.map((e) => (
            <div key={e.edition_id} className="flex gap-3 items-center border border-ink-500 p-3 w-[340px]">
              <Cover url={e.cover_image_url} className="w-12 h-16 shrink-0" />
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="font-serif text-[14px] text-paper truncate">{e.title}</div>
                <div className="font-mono text-[10px] text-paper-trace">{e.issue_label}</div>
                {e.has_announcements ? (
                  <Link to={`/issue-announcements/${e.edition_id}`} className={`${ACTION_QUIET} inline-block`}>Open</Link>
                ) : (
                  <button type="button" className={ACTION_GREEN} disabled={create.isPending}
                          onClick={() => create.mutate(e.edition_id)}>
                    {create.isPending && create.variables === e.edition_id ? 'Drafting…' : 'Create announcement emails'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="label">Announcements</div>
        {list.isLoading && <div className="label text-paper-trace">Loading…</div>}
        {list.error && <div className="font-mono text-[11px] text-radar-red">{apiErrorText(list.error)}</div>}
        {list.data && list.data.items.length === 0 && (
          <div className="font-mono text-[11px] text-paper-trace">None yet.</div>
        )}
        {list.data && list.data.items.length > 0 && shownItems?.length === 0 && (
          <div className="font-mono text-[11px] text-paper-trace">None for this title.</div>
        )}
        {shownItems && shownItems.length > 0 && (
          <div className="border border-ink-500 bg-ink-800/40">
            <table className="w-full text-[13px] font-mono">
              <thead>
                <tr className="border-b border-ink-500/60 bg-ink-800/60">
                  <th className="label py-2 px-4 font-normal text-left">Issue</th>
                  <th className="label py-2 px-3 font-normal text-left">{AUDIENCE_LABEL.active}</th>
                  <th className="label py-2 px-3 font-normal text-left">{AUDIENCE_LABEL.expired}</th>
                </tr>
              </thead>
              <tbody>
                {shownItems.map((it) => (
                  <tr key={it.edition_id} className="border-b border-ink-500/40 hover:bg-ink-700/30">
                    <td className="py-2 px-4">
                      <Link to={`/issue-announcements/${it.edition_id}`} className="flex items-center gap-3 group">
                        <Cover url={it.cover_image_url} className="w-8 h-11 shrink-0" />
                        <span>
                          <span className="block font-serif text-[14px] text-paper group-hover:text-amber-glow">{it.title}</span>
                          <span className="block text-[10px] text-paper-trace">{it.issue_label}</span>
                        </span>
                      </Link>
                    </td>
                    <td className="py-2 px-3"><StatusChip status={it.audiences.active?.status} /></td>
                    <td className="py-2 px-3"><StatusChip status={it.audiences.expired?.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

// -------------------------------------------------------------- review view

function ReviewView({ editionId }: { editionId: number }) {
  const qc = useQueryClient()
  const detail = useQuery({
    queryKey: ['issue-announcements', 'detail', editionId],
    queryFn: () => api.get<AnnouncementDetail>(`/api/issue-announcements/editions/${editionId}`),
  })
  const me = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  const isAdmin = me.data?.role === 'admin'
  const redraft = useMutation({
    mutationFn: () => api.post<AnnouncementDetail>(`/api/issue-announcements/editions/${editionId}/redraft`),
    onSuccess: () => invalidateAll(qc),
  })

  if (detail.isLoading) return <div className="p-8 label text-paper-trace">Loading…</div>
  if (detail.error || !detail.data) {
    return (
      <div className="p-8 space-y-3">
        <div className="font-mono text-[11px] text-radar-red">{apiErrorText(detail.error)}</div>
        <Link to="/issue-announcements" className="label-amber hover:underline">← Issue announcements</Link>
      </div>
    )
  }
  const { edition, audiences } = detail.data
  const allDrafts = audiences.every((a) => canEdit(a.campaign_status))
  const ordered = [...audiences].sort((a, b) => (a.audience === 'active' ? -1 : b.audience === 'active' ? 1 : 0))

  return (
    <div className="p-8 space-y-6 max-w-[1600px]">
      <Link to="/issue-announcements" className="label-amber hover:underline">← Issue announcements</Link>
      <header className="flex items-end gap-5 border-b border-ink-500 pb-6">
        <Cover url={edition.cover_image_url} className="w-20 h-28 shrink-0" />
        <div className="flex-1">
          <div className="label mb-1">Issue announcement · {edition.issue_label}</div>
          <h1 className="font-serif text-[34px] leading-[1.05] tracking-tight text-paper">{edition.title}</h1>
          {edition.status !== 'active' && (
            <div className="font-mono text-[11px] text-amber-glow mt-2">This issue is not published to members.</div>
          )}
        </div>
        <div className="space-y-1 text-right">
          <button type="button" className={ACTION_QUIET} disabled={!allDrafts || redraft.isPending}
                  title={allDrafts ? undefined : 'Only drafts can be redrafted'}
                  onClick={() => { if (window.confirm("This replaces both drafts' copy. Continue?")) redraft.mutate() }}>
            {redraft.isPending ? 'Redrafting…' : 'Redraft both with AI'}
          </button>
          {redraft.error && <div className="font-mono text-[10px] text-radar-red">{apiErrorText(redraft.error)}</div>}
        </div>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {ordered.map((a) => (
          <AudienceColumn key={a.id} a={a} editionId={editionId} isAdmin={isAdmin} />
        ))}
      </div>
    </div>
  )
}

function AudienceColumn({ a, editionId, isAdmin }: { a: AudienceDetail; editionId: number; isAdmin: boolean }) {
  const qc = useQueryClient()
  const editable = canEdit(a.campaign_status)
  const count = useQuery({
    queryKey: ['ia-count', a.id, 'now'],
    queryFn: () => api.get<Count>(`/api/issue-announcements/${a.id}/count`),
  })
  const sample = useQuery({
    queryKey: ['issue-announcements', 'sample', a.id],
    queryFn: () => api.get<Sample>(`/api/issue-announcements/${a.id}/sample?limit=50`),
  })
  const [picked, setPicked] = useState<number | null>(null)
  const subscriberId = picked ?? sample.data?.items[0]?.id ?? null
  const [approveOpen, setApproveOpen] = useState(false)

  const cancel = useMutation({
    mutationFn: () => api.post<AnnouncementDetail>(`/api/issue-announcements/${a.id}/cancel`),
    onSuccess: () => invalidateAll(qc),
  })

  return (
    <section className="border border-ink-500 p-4 space-y-5 min-w-0">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="font-serif text-[22px] text-paper">{AUDIENCE_LABEL[a.audience]}</h2>
        <StatusChip status={a.campaign_status} />
      </div>
      {a.copy_source === 'fallback' && (
        <div className="border border-amber-glow/60 bg-amber-glow/10 px-3 py-2 font-mono text-[11px] text-amber-glow">
          AI copy wasn't available — this is the plain version. Edit before sending.
        </div>
      )}

      <div className="font-mono text-[12px] text-paper-dim">
        {count.isLoading && 'Counting recipients…'}
        {count.error && <span className="text-radar-red">{apiErrorText(count.error)}</span>}
        {count.data && `${fmtInt(count.data.count)} recipients · about ${count.data.estimated_minutes} min to send`}
      </div>

      {a.campaign_status === 'scheduled' && (
        <div className="border border-radar-cyan/40 p-3 space-y-2">
          <div className="font-mono text-[12px] text-radar-cyan">
            Scheduled for {fmtDateTime(a.scheduled_for)}{a.approved_by ? ` · approved by ${a.approved_by}` : ''}
          </div>
          <div className="flex gap-3 items-center">
            <button type="button" className={ACTION_QUIET} disabled={cancel.isPending} onClick={() => cancel.mutate()}>
              {cancel.isPending ? 'Cancelling…' : 'Cancel'}
            </button>
            {isAdmin && a.campaign_id && (
              <Link to={`/campaigns/${a.campaign_id}/results`} className="label-amber hover:underline">Campaign results →</Link>
            )}
          </div>
        </div>
      )}
      {a.campaign_status === 'cancelled' && (
        <div className="border border-ink-500 p-3 space-y-2">
          <div className="font-mono text-[11px] text-paper-dim">This send was cancelled. Return it to a draft to edit and approve it again.</div>
          <button type="button" className={ACTION_QUIET} disabled={cancel.isPending} onClick={() => cancel.mutate()}>
            {cancel.isPending ? 'Working…' : 'Back to draft'}
          </button>
        </div>
      )}
      {(a.campaign_status === 'running' || a.campaign_status === 'completed') && (
        <div className="font-mono text-[12px] text-paper-dim flex gap-3 items-center flex-wrap">
          <span>
            {a.campaign_status === 'running' ? 'Sending now' : 'Sent'}
            {a.approved_by ? ` · approved by ${a.approved_by}` : ''}
          </span>
          {isAdmin && a.campaign_id && (
            <Link to={`/campaigns/${a.campaign_id}/results`} className="label-amber hover:underline">Campaign results →</Link>
          )}
        </div>
      )}
      {cancel.error && <div className="font-mono text-[11px] text-radar-red">{apiErrorText(cancel.error)}</div>}

      {/* Keyed by the saved copy so a save or redraft resets the form to what the server holds. */}
      <CopyForm key={JSON.stringify(a.copy)} a={a} editable={editable} editionId={editionId} />

      <div className="space-y-2">
        <div className="label">Preview as</div>
        {sample.error && <div className="font-mono text-[11px] text-radar-red">{apiErrorText(sample.error)}</div>}
        {sample.data && sample.data.items.length === 0 && (
          <div className="font-mono text-[11px] text-paper-trace">No one is in this audience right now.</div>
        )}
        {sample.data && sample.data.items.length > 0 && (
          <select className={INPUT} value={subscriberId ?? ''} onChange={(e) => setPicked(Number(e.target.value))}>
            {sample.data.items.map((s) => (
              <option key={s.id} value={s.id}>
                {[s.first_name, s.last_name].filter(Boolean).join(' ') || '(no name)'} · {s.email ?? 'no email'}
              </option>
            ))}
          </select>
        )}
        {subscriberId != null && <PreviewPane annId={a.id} subscriberId={subscriberId} copyKey={JSON.stringify(a.copy)} />}
      </div>

      <TestSend annId={a.id} subscriberId={subscriberId} isAdmin={isAdmin} />

      {editable && (
        <div className="border-t border-ink-500 pt-4">
          <Button size="sm" onClick={() => setApproveOpen(true)}>Approve &amp; send…</Button>
        </div>
      )}
      {approveOpen && <ApproveModal a={a} onClose={() => setApproveOpen(false)} />}
    </section>
  )
}

function CopyForm({ a, editable, editionId }: { a: AudienceDetail; editable: boolean; editionId: number }) {
  const qc = useQueryClient()
  const [copy, setCopy] = useState<Copy>(a.copy)
  const problems = copyProblems(copy)
  const dirty = JSON.stringify(copy) !== JSON.stringify(a.copy)
  const save = useMutation({
    mutationFn: () => api.put<AnnouncementDetail>(`/api/issue-announcements/${a.id}/copy`, copy),
    onSuccess: (data) => {
      qc.setQueryData(['issue-announcements', 'detail', editionId], data)
      invalidateAll(qc)
    },
  })
  const set = (patch: Partial<Copy>) => setCopy((c) => ({ ...c, ...patch }))
  const setHl = (i: number, patch: Partial<Highlight>) =>
    set({ highlights: copy.highlights.map((h, j) => (j === i ? { ...h, ...patch } : h)) })

  return (
    <fieldset disabled={!editable} className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="label">Copy</span>
        {!editable && <span className="font-mono text-[10px] text-paper-trace">Only drafts can be edited.</span>}
      </div>
      <LimitedInput label="Subject" value={copy.subject} limit={LIMITS.subject} onChange={(v) => set({ subject: v })} />
      <LimitedInput label="Preview text" value={copy.preheader} limit={LIMITS.preheader} onChange={(v) => set({ preheader: v })} />
      <label className="block space-y-1">
        <span className="label block">Intro <Counter n={charCount(copy.intro)} limit={LIMITS.intro} /></span>
        <textarea className={`${INPUT} min-h-[96px]`} value={copy.intro} onChange={(e) => set({ intro: e.target.value })} />
      </label>
      <LimitedInput label="Button label" value={copy.cta_label} limit={LIMITS.cta_label} onChange={(v) => set({ cta_label: v })} />

      <div className="space-y-2">
        <div className="label">Highlights</div>
        {copy.highlights.length === 0 && (
          <div className="font-mono text-[10px] text-paper-trace">No highlights — the email goes without a story list.</div>
        )}
        {copy.highlights.map((h, i) => (
          <div key={i} className="border border-ink-500 p-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] text-paper-trace">{i + 1}</span>
              <span className="flex-1" />
              <button type="button" className={ACTION_QUIET} disabled={i === 0}
                      onClick={() => set({ highlights: moveHighlight(copy.highlights, i, i - 1) })}>↑</button>
              <button type="button" className={ACTION_QUIET} disabled={i === copy.highlights.length - 1}
                      onClick={() => set({ highlights: moveHighlight(copy.highlights, i, i + 1) })}>↓</button>
              <button type="button" className={`${ACTION} border-radar-red/50 text-radar-red hover:bg-radar-red/10`}
                      onClick={() => set({ highlights: copy.highlights.filter((_, j) => j !== i) })}>Delete</button>
            </div>
            <input className={INPUT} placeholder="Title" value={h.title} onChange={(e) => setHl(i, { title: e.target.value })} />
            <input className={INPUT} placeholder="Hook (one line)" value={h.hook} onChange={(e) => setHl(i, { hook: e.target.value })} />
            <input className={INPUT} placeholder="Byline (optional)" value={h.byline} onChange={(e) => setHl(i, { byline: e.target.value })} />
          </div>
        ))}
        <button type="button" className={ACTION_QUIET} disabled={copy.highlights.length >= MAX_HIGHLIGHTS}
                onClick={() => set({ highlights: [...copy.highlights, { title: '', hook: '', byline: '' }] })}>
          Add highlight
        </button>
      </div>

      {problems.length > 0 && (
        <ul className="font-mono text-[11px] text-amber-glow list-disc pl-5">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
      {save.error && <div className="font-mono text-[11px] text-radar-red">{apiErrorText(save.error)}</div>}
      {editable && (
        <div className="flex gap-3 items-center">
          <Button size="sm" disabled={!dirty || problems.length > 0 || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {dirty && (
            <button type="button" className="font-mono text-[11px] uppercase tracking-label text-paper-trace hover:text-paper"
                    onClick={() => setCopy(a.copy)}>Discard changes</button>
          )}
        </div>
      )}
    </fieldset>
  )
}

function Counter({ n, limit }: { n: number; limit: number }) {
  return <span className={`ml-2 font-mono text-[10px] ${n > limit ? 'text-radar-red' : 'text-paper-trace'}`}>{n}/{limit}</span>
}

function LimitedInput({ label, value, limit, onChange }: { label: string; value: string; limit: number; onChange: (v: string) => void }) {
  return (
    <label className="block space-y-1">
      <span className="label block">{label} <Counter n={charCount(value)} limit={limit} /></span>
      <input className={INPUT} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  )
}

function PreviewPane({ annId, subscriberId, copyKey }: { annId: number; subscriberId: number; copyKey: string }) {
  const preview = useQuery({
    // copyKey: a saved edit or redraft re-renders the preview.
    queryKey: ['issue-announcements', 'preview', annId, subscriberId, copyKey],
    queryFn: () => api.post<Preview>(`/api/issue-announcements/${annId}/preview`, { subscriber_id: subscriberId }),
  })
  if (preview.isLoading) return <div className="label text-paper-trace">Rendering…</div>
  if (preview.error) return <div className="font-mono text-[11px] text-radar-red">{apiErrorText(preview.error)}</div>
  if (!preview.data) return null
  return (
    <div className="space-y-1.5">
      <div className="font-mono text-[11px] text-paper-dim">
        <span className="text-paper-trace">From</span> {preview.data.from_email}
        <span className="text-paper-trace ml-3">Subject</span> <span className="text-paper">{preview.data.subject}</span>
      </div>
      <div className="font-mono text-[10px] text-paper-trace">Previews use sample links, not the member's personal sign-in link.</div>
      <iframe title="Email preview" sandbox="" srcDoc={preview.data.html} className="w-full h-[640px] bg-white border border-ink-500" />
    </div>
  )
}

function TestSend({ annId, subscriberId, isAdmin }: { annId: number; subscriberId: number | null; isAdmin: boolean }) {
  // /api/test-pools is admin-only (operators are outside its scope), so the pool choice is offered to admins only.
  const pools = useQuery({
    queryKey: ['test-pools'],
    queryFn: () => api.get<{ items: TestPool[] }>('/api/test-pools'),
    enabled: isAdmin,
  })
  const [poolId, setPoolId] = useState<number | null>(null)
  const send = useMutation({
    mutationFn: () => api.post<TestResult>(`/api/issue-announcements/${annId}/test-send`, {
      ...(subscriberId != null ? { subscriber_id: subscriberId } : {}),
      ...(poolId != null ? { test_pool_id: poolId } : {}),
    }),
  })
  return (
    <div className="space-y-2">
      <div className="label">Test email</div>
      <div className="flex flex-wrap gap-3 items-center">
        {isAdmin && (
          <select className={`${INPUT} w-auto`} value={poolId ?? ''} onChange={(e) => setPoolId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">To me</option>
            {pools.data?.items.map((p) => (
              <option key={p.id} value={p.id}>Test pool: {p.name} ({p.member_count})</option>
            ))}
          </select>
        )}
        <button type="button" className={ACTION_GREEN} disabled={send.isPending} onClick={() => send.mutate()}>
          {send.isPending ? 'Sending…' : poolId != null ? 'Send test to pool' : 'Send test to me'}
        </button>
      </div>
      <div className="font-mono text-[10px] text-paper-trace">
        Test emails use sample links, not the member's personal sign-in link. Personalized as the member picked above.
      </div>
      {send.error && <div className="font-mono text-[11px] text-radar-red">{apiErrorText(send.error, true)}</div>}
      {send.data && (
        <div className={`font-mono text-[11px] ${send.data.failed_count ? 'text-amber-glow' : 'text-radar-green'}`}>
          Sent {send.data.sent_count}{send.data.failed_count ? `, ${send.data.failed_count} failed` : ''} · to {send.data.to_emails.join(', ')}
        </div>
      )}
    </div>
  )
}

function ApproveModal({ a, onClose }: { a: AudienceDetail; onClose: () => void }) {
  const qc = useQueryClient()
  const [mode, setMode] = useState<'now' | 'at'>('now')
  const [local, setLocal] = useState('')
  const [typed, setTyped] = useState('')
  const sendAt = mode === 'at' ? localInputToIso(local) : null
  const count = useQuery({
    queryKey: ['ia-count', a.id, sendAt ?? 'now'],
    queryFn: () => api.get<Count>(`/api/issue-announcements/${a.id}/count${sendAt ? `?at=${encodeURIComponent(sendAt)}` : ''}`),
    enabled: mode === 'now' || !!sendAt,
  })
  const phrase = count.data ? confirmPhrase(count.data.sendable_count) : null
  const approve = useMutation({
    mutationFn: () => api.post<AnnouncementDetail>(`/api/issue-announcements/${a.id}/approve`, {
      send_at: sendAt, confirm_token: phrase,
    }),
    onSuccess: () => {
      invalidateAll(qc)
      onClose()
    },
  })
  const ready = !!phrase && typed.trim() === phrase && (mode === 'now' || !!sendAt) && !approve.isPending

  return (
    <div className="fixed inset-0 bg-ink-900/80 flex items-center justify-center z-50 p-6" onClick={onClose}>
      <div className="bg-ink-800 border border-ink-500 w-full max-w-[520px] p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="label-amber">Approve &amp; send · {AUDIENCE_LABEL[a.audience]}</div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={mode === 'now'} onChange={() => setMode('now')} />
            <span className="label">Send now</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={mode === 'at'} onChange={() => setMode('at')} />
            <span className="label">Send at</span>
          </label>
          {mode === 'at' && (
            <input type="datetime-local" className={`${INPUT} w-auto ml-6`} value={local} onChange={(e) => setLocal(e.target.value)} />
          )}
        </div>
        <div className="font-mono text-[12px] text-paper-dim">
          {count.isLoading && 'Counting recipients…'}
          {count.error && <span className="text-radar-red">{apiErrorText(count.error)}</span>}
          {count.data && (
            <>
              {fmtInt(count.data.count)} recipients · {fmtInt(count.data.sendable_count)} can be sent to · about {count.data.estimated_minutes} min to send
            </>
          )}
        </div>
        {phrase && (
          <label className="block space-y-1">
            <span className="label block">Type <span className="text-paper">{phrase}</span> to confirm</span>
            <input className={INPUT} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={phrase} />
          </label>
        )}
        {approve.error && <div className="font-mono text-[11px] text-radar-red">{apiErrorText(approve.error)}</div>}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="outline" size="sm" disabled={approve.isPending} onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={!ready} onClick={() => approve.mutate()}>
            {approve.isPending ? 'Approving…' : mode === 'now' ? 'Approve & send now' : 'Approve & schedule'}
          </Button>
        </div>
      </div>
    </div>
  )
}
